import React, { useState, useEffect } from "react";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { CUSTOM_MESSAGE, RODEPICK_API_CONST } from "src/_config";
import OTPVerificationModal from "./OTPVerificationModal";
import EmailLoginModal from "./EmailLoginModal";
import AuthModalHeader from "./AuthModalHeader";
import AuthModalFooter from "./AuthModalFooter";

interface SignInModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSendCodeSuccess?: (phoneNumber: string) => void;
  onAuthSuccess?: () => void;
}

const SignInModal: React.FC<SignInModalProps> = ({ isOpen, onClose, onSendCodeSuccess, onAuthSuccess }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [consent, setConsent] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState("");

  const userApi = useUserApi();
  const toast = useToaster();

  // Reset all form state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhoneNumber("");
      setConsent(true);
      setIsLoading(false);
      setShowOTPModal(false);
      setShowEmailModal(false);
      setVerifiedPhoneNumber("");
    }
  }, [isOpen]);

  const handleSendCode = async () => {
    if (!phoneNumber.trim()) {
      toast.error("Please enter your mobile number");
      return;
    }

    setIsLoading(true);

    // Extract clean phone number (digits only) and get last 10 digits - same logic as LoginForm.tsx
    const mobileNoDigit = phoneNumber.replace(/\D/g, "").slice(-10);

    // Prepare API parameters - same as LoginForm.tsx
    const params = {
      phone: parseInt(mobileNoDigit),
      country_code: 1, // Default to US/Canada, same as LoginForm.tsx default
      game_slug: RODEPICK_API_CONST, // Add game_slug constant as requested
    };

    // Call the same login API as LoginForm.tsx
    userApi.callLogin(
      params,
      (message: string, resp: any) => {
        setIsLoading(false);
        if (resp) {
          toast.success(CUSTOM_MESSAGE.SEND_OTP);
          // Store the phone number and open OTP modal
          setVerifiedPhoneNumber(mobileNoDigit);
          // Open OTP modal (SignInModal will hide itself when OTP modal is shown)
          setShowOTPModal(true);
          // Also call the callback if provided for backward compatibility
          onSendCodeSuccess?.(mobileNoDigit);
        } else {
          toast.error(message);
        }
      },
      (message: string) => {
        setIsLoading(false);
        toast.error(message);
      },
    );
  };

  const handleEmailLogin = () => {
    // Open email login modal (signin modal will be hidden due to the rendering condition)
    setShowEmailModal(true);
  };

  const handleEmailSuccess = () => {
    // Close email modal and call success callback
    setShowEmailModal(false);
    onAuthSuccess?.();
  };

  const handleEmailClose = () => {
    // Close email modal and the entire modal flow
    setShowEmailModal(false);
    onClose(); // Close the parent SignIn modal
  };

  const handleSwitchToPhone = () => {
    // Close email modal and show phone login modal
    setShowEmailModal(false);
    // The main SignInModal should already be open (isOpen=true)
  };

  const handleOTPSuccess = () => {
    // Close both modals and call success callback
    setShowOTPModal(false);
    onAuthSuccess?.();
  };

  const handleOTPClose = () => {
    // Close OTP modal and reset state
    setShowOTPModal(false);
    // Reset form state when OTP modal is closed
    setPhoneNumber("");
    setConsent(false);
    setVerifiedPhoneNumber("");
    // Close the parent SignIn modal
    onClose();
  };

  const handleResendCode = () => {
    // Resend OTP using the same phone number
    const params = {
      phone: parseInt(verifiedPhoneNumber),
      country_code: 1,
      game_slug: RODEPICK_API_CONST,
    };

    userApi.callLogin(
      params,
      (message: string, resp: any) => {
        if (resp) {
          toast.success(CUSTOM_MESSAGE.RESENT_OTP);
        } else {
          toast.error(message);
        }
      },
      (message: string) => {
        toast.error(message);
      },
    );
  };

  // Only show signin modal when isOpen is true and OTP modal is not showing
  // Show OTP modal when showOTPModal is true
  //if (!isOpen && !showOTPModal) return null;

  return (
    <>
      {/* Sign In Modal - only show when isOpen is true and OTP modal is not showing and email modal is not showing */}
      {isOpen && !showOTPModal && !showEmailModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content signin-modal">
                <div className="modal-header border-0">
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={onClose}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body text-center">
                  {/* Header with Logo */}
                  <AuthModalHeader />
                  {/* Lock Icon */}
                  <div className="signin-icon mb-3">
                    <div className="lock-icon">
                      <i className="fas fa-lock"></i>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 className="signin-title mb-2">Sign In</h2>
                  <p className="signin-subtitle mb-4">Welcome to the arena</p>

                  {/* Phone Input */}
                  <div className="phone-input-container mb-3">
                    <div className="input-group">
                      <span className="input-group-text phone-prefix">
                        <i className="fas fa-phone"></i>
                      </span>
                      <input
                        type="tel"
                        className="form-control phone-input"
                        placeholder="(999) 999-9999"
                        value={phoneNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          let formattedValue = "";

                          if (value.length > 0) {
                            if (value.length <= 3) {
                              formattedValue = `(${value}`;
                            } else if (value.length <= 6) {
                              formattedValue = `(${value.slice(0, 3)}) ${value.slice(3)}`;
                            } else {
                              formattedValue = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
                            }
                          }

                          setPhoneNumber(formattedValue);
                        }}
                        maxLength={14}
                      />
                    </div>
                  </div>

                  {/* Consent Checkbox */}
                  <div className="consent-container mb-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="consentCheck"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                      />
                      <label className="form-check-label consent-text" htmlFor="consentCheck">
                        Yes, I consent to receive promotional text messages from Inside Rodeo. I understand that I am
                        not required to grant consent as a condition of any purchase. Msg frequency varies. Msg & Data
                        rates may apply. Reply STOP to cancel. Reply HELP for help. Msg frequency varies. Msg & Data
                        rates May apply. View our{" "}
                        <a
                          href="/cms/terms-and-conditions"
                          className="consent-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms and Conditions
                        </a>
                        ,{" "}
                        <a
                          href="/cms/privacy-policy"
                          className="consent-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Privacy Policy
                        </a>
                        .
                      </label>
                    </div>
                  </div>

                  {/* Send Code Button */}
                  <button
                    className="btn btn-primary send-code-btn w-100 mb-3"
                    onClick={handleSendCode}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending Code...
                      </>
                    ) : (
                      "Send Code"
                    )}
                  </button>

                  {/* Email Login Link */}
                  <div className="email-login-container">
                    <button className="btn btn-link email-login-btn" onClick={handleEmailLogin}>
                      Login with email/password
                    </button>
                  </div>

                  {/* Footer Links */}
                  <AuthModalFooter />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
      {/* OTP Verification Modal - only show when showOTPModal is true */}
      <OTPVerificationModal
        isOpen={showOTPModal}
        onClose={handleOTPClose}
        phoneNumber={verifiedPhoneNumber}
        onVerifySuccess={handleOTPSuccess}
        onResendCode={handleResendCode}
        consent={consent}
      />
      {/* Email Login Modal - only show when showEmailModal is true */}
      <EmailLoginModal
        isOpen={showEmailModal}
        onClose={handleEmailClose}
        onLoginSuccess={handleEmailSuccess}
        onSwitchToPhone={handleSwitchToPhone}
      />
    </>
  );
};

export default SignInModal;
